import type { SVGProps } from 'react';

type IconProps = SVGProps<SVGSVGElement>;

function Base({ children, ...props }: IconProps & { children: React.ReactNode }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      {children}
    </svg>
  );
}

export function Menu(props: IconProps) {
  return (
    <Base {...props}>
      <line x1="4" y1="6" x2="20" y2="6" />
      <line x1="4" y1="12" x2="20" y2="12" />
      <line x1="4" y1="18" x2="20" y2="18" />
    </Base>
  );
}

export function X(props: IconProps) {
  return (
    <Base {...props}>
      <line x1="6" y1="6" x2="18" y2="18" />
      <line x1="18" y1="6" x2="6" y2="18" />
    </Base>
  );
}

export function Spinner({ className, ...props }: IconProps) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      className={`animate-spin ${className ?? ''}`}
      {...props}
    >
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2.5" opacity="0.25" />
      <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
    </svg>
  );
}

export function ExternalLink(props: IconProps) {
  return (
    <Base {...props}>
      <path d="M15 3h6v6" />
      <path d="M10 14L21 3" />
      <path d="M21 14v5a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5" />
    </Base>
  );
}

export function Ticket(props: IconProps) {
  return (
    <Base {...props}>
      <path d="M3 9a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v1a2 2 0 0 0 0 4v1a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-1a2 2 0 0 0 0-4V9z" />
      <path d="M13 7v10" strokeDasharray="2 2" />
    </Base>
  );
}

export function CheckCircle(props: IconProps) {
  return (
    <Base {...props}>
      <path d="M21.5 11.1V12a9.5 9.5 0 1 1-5.6-8.7" />
      <path d="M22 5l-10 10-3-3" />
    </Base>
  );
}

export function Download(props: IconProps) {
  return (
    <Base {...props}>
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <path d="M7 10l5 5 5-5" />
      <path d="M12 15V3" />
    </Base>
  );
}

export function Eye(props: IconProps) {
  return (
    <Base {...props}>
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z" />
      <circle cx="12" cy="12" r="3" />
    </Base>
  );
}

export function EyeOff(props: IconProps) {
  return (
    <Base {...props}>
      <path d="M17.94 17.94A10.94 10.94 0 0 1 12 19c-6.5 0-10-7-10-7a18.5 18.5 0 0 1 4.22-5.06" />
      <path d="M9.9 4.24A10.94 10.94 0 0 1 12 4c6.5 0 10 7 10 7a18.4 18.4 0 0 1-3.17 4.19" />
      <path d="M10.58 10.58a2 2 0 1 0 2.83 2.83" />
      <line x1="2" y1="2" x2="22" y2="22" />
    </Base>
  );
}

export function Construction(props: IconProps) {
  return (
    <Base {...props}>
      <rect x="2" y="14" width="20" height="8" rx="1" />
      <path d="M4 14v-3a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v3" />
      <path d="M6 9V6a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v3" />
      <line x1="10" y1="14" x2="10" y2="22" />
      <line x1="14" y1="14" x2="14" y2="22" />
    </Base>
  );
}

export function Database(props: IconProps) {
  return (
    <Base {...props}>
      <ellipse cx="12" cy="5" rx="9" ry="3" />
      <path d="M3 5v14a9 3 0 0 0 18 0V5" />
      <path d="M3 12a9 3 0 0 0 18 0" />
    </Base>
  );
}

export function Building2(props: IconProps) {
  return (
    <Base {...props}>
      <path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z" />
      <path d="M6 12H4a2 2 0 0 0-2 2v8h4" />
      <path d="M18 9h2a2 2 0 0 1 2 2v11h-4" />
      <line x1="10" y1="6" x2="14" y2="6" />
      <line x1="10" y1="10" x2="14" y2="10" />
      <line x1="10" y1="14" x2="14" y2="14" />
      <line x1="10" y1="18" x2="14" y2="18" />
    </Base>
  );
}

/** 文件夹。复现包页的文件结构树用。 */
export function Folder(props: IconProps) {
  return (
    <Base strokeWidth={1.7} {...props}>
      <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2Z" />
    </Base>
  );
}

/** 包裹箱。完整复现包入口用。 */
export function Package(props: IconProps) {
  return (
    <Base strokeWidth={1.7} {...props}>
      <path d="M21 8.5v7a2 2 0 0 1-1.05 1.76l-7 3.8a2 2 0 0 1-1.9 0l-7-3.8A2 2 0 0 1 3 15.5v-7a2 2 0 0 1 1.05-1.76l7-3.8a2 2 0 0 1 1.9 0l7 3.8A2 2 0 0 1 21 8.5Z" />
      <path d="m3.3 7.5 8.7 4.7 8.7-4.7" />
      <path d="M12 12.2V21" />
    </Base>
  );
}

/** 代码（尖括号）。 */
export function Code(props: IconProps) {
  return (
    <Base strokeWidth={1.9} {...props}>
      <path d="m9 8-4 4 4 4" />
      <path d="m15 8 4 4-4 4" />
    </Base>
  );
}

/**
 * 省级数据图标：折叠地图。
 * 与 MapPin 一同从 Header.tsx 提到这里（2026-09-23）——首页 Hero 的分类条也要用同一套，
 * 三个一级分类靠「楼宇 / 地图 / 定位」编码观测实体是公司、省、市，站内必须是同一组图形。
 * 描边 1.7 而非 Base 的 2：这两枚笔画密度高，2 在小尺寸下会糊成一团。
 */
export function MapFolded(props: IconProps) {
  return (
    <Base strokeWidth={1.7} {...props}>
      <path d="M9 4 3 6.2v13.6l6-2.2 6 2.2 6-2.2V6L15 8.2 9 6Z" />
      <path d="M9 4v13.8" />
      <path d="M15 8.2V22" />
    </Base>
  );
}

/** 地级市数据图标：定位标记。 */
export function MapPin(props: IconProps) {
  return (
    <Base strokeWidth={1.7} {...props}>
      <path d="M12 21.5s6.5-5.8 6.5-11a6.5 6.5 0 1 0-13 0c0 5.2 6.5 11 6.5 11Z" />
      <circle cx="12" cy="10.5" r="2.4" />
    </Base>
  );
}

export function TrendingUp(props: IconProps) {
  return (
    <Base {...props}>
      <polyline points="22 7 13.5 15.5 8.5 10.5 2 17" />
      <polyline points="16 7 22 7 22 13" />
    </Base>
  );
}

export function Shield(props: IconProps) {
  return (
    <Base {...props}>
      <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67 0C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" />
    </Base>
  );
}

export function ChevronRight(props: IconProps) {
  return (
    <Base {...props}>
      <polyline points="9 18 15 12 9 6" />
    </Base>
  );
}

export function FileText(props: IconProps) {
  return (
    <Base {...props}>
      <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="8" y1="13" x2="16" y2="13" />
      <line x1="8" y1="17" x2="16" y2="17" />
      <line x1="8" y1="9" x2="10" y2="9" />
    </Base>
  );
}

export function GitBranch(props: IconProps) {
  return (
    <Base {...props}>
      <line x1="6" x2="6" y1="3" y2="15" />
      <circle cx="18" cy="6" r="3" />
      <circle cx="6" cy="18" r="3" />
      <path d="M18 9a9 9 0 0 1-9 9" />
    </Base>
  );
}

export function Search(props: IconProps) {
  return (
    <Base {...props}>
      <circle cx="11" cy="11" r="7" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </Base>
  );
}

export function ShoppingCart(props: IconProps) {
  return (
    <Base {...props}>
      <circle cx="8" cy="21" r="1" />
      <circle cx="19" cy="21" r="1" />
      <path d="M2.05 2.05h2l2.66 12.42a2 2 0 0 0 2 1.58h9.78a2 2 0 0 0 1.95-1.57l1.65-7.43H5.12" />
    </Base>
  );
}

export function CreditCard(props: IconProps) {
  return (
    <Base {...props}>
      <rect width="20" height="14" x="2" y="5" rx="2" />
      <line x1="2" y1="10" x2="22" y2="10" />
    </Base>
  );
}

export function Clock(props: IconProps) {
  return (
    <Base {...props}>
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </Base>
  );
}

export function Unlock(props: IconProps) {
  return (
    <Base {...props}>
      <rect width="18" height="11" x="3" y="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0 1 9.9-1" />
    </Base>
  );
}

export function Trash2(props: IconProps) {
  return (
    <Base {...props}>
      <path d="M3 6h18" />
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
      <line x1="10" y1="11" x2="10" y2="17" />
      <line x1="14" y1="11" x2="14" y2="17" />
    </Base>
  );
}

export function Sun(props: IconProps) {
  return (
    <Base {...props}>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2" />
      <path d="M12 20v2" />
      <path d="m4.93 4.93 1.41 1.41" />
      <path d="m17.66 17.66 1.41 1.41" />
      <path d="M2 12h2" />
      <path d="M20 12h2" />
      <path d="m6.34 17.66-1.41 1.41" />
      <path d="m19.07 4.93-1.41 1.41" />
    </Base>
  );
}

export function Moon(props: IconProps) {
  return (
    <Base {...props}>
      <path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z" />
    </Base>
  );
}

export function Monitor(props: IconProps) {
  return (
    <Base {...props}>
      <rect width="20" height="14" x="2" y="3" rx="2" />
      <line x1="8" y1="21" x2="16" y2="21" />
      <line x1="12" y1="17" x2="12" y2="21" />
    </Base>
  );
}

export function ChevronDown(props: IconProps) {
  return (
    <Base {...props}>
      <polyline points="6 9 12 15 18 9" />
    </Base>
  );
}

export function User(props: IconProps) {
  return (
    <Base {...props}>
      <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </Base>
  );
}

export function LogOut(props: IconProps) {
  return (
    <Base {...props}>
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <polyline points="16 17 21 12 16 7" />
      <line x1="21" y1="12" x2="9" y2="12" />
    </Base>
  );
}

export function ScanLine(props: IconProps) {
  return (
    <Base {...props}>
      <path d="M3 7V5a2 2 0 0 1 2-2h2" />
      <path d="M17 3h2a2 2 0 0 1 2 2v2" />
      <path d="M21 17v2a2 2 0 0 1-2 2h-2" />
      <path d="M7 21H5a2 2 0 0 1-2-2v-2" />
      <line x1="7" y1="12" x2="17" y2="12" />
    </Base>
  );
}

export function AlertCircle(props: IconProps) {
  return (
    <Base {...props}>
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8" x2="12" y2="12" />
      <line x1="12" y1="16" x2="12.01" y2="16" />
    </Base>
  );
}

/** 站内统一的"前往/进入"箭头（来自 assets/arrow.svg：圆环 + 右向 chevron）。
 *  用 fill=currentColor，颜色随上下文文字色走（含夜间模式）。 */
export function Arrow(props: IconProps) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 1024 1024"
      fill="currentColor"
      aria-hidden="true"
      {...props}
    >
      <path d="M512 64h-5.8c-9.6 0.2-19.2 0.6-28.7 1.3l-5.6 0.5C313.1 79.9 178.2 176.7 110.6 312.9l-2.4 4.9a445.7 445.7 0 0 0-20.7 50.5c-0.5 1.7-1.1 3.5-1.7 5.2l-3.3 10.6c-3.1 10.5-5.9 21.3-8.2 32.2-0.8 3.6-1.6 7.2-2.3 10.9a455.7 455.7 0 0 0-8 84.8v5.8c3 231.3 181.1 420.3 407.9 440.4l5.6 0.5c9.5 0.7 19.1 1.1 28.7 1.3h5.8c15.5 0 30.7-0.8 45.8-2.3l11.3-1.3c9.3-1.2 18.6-2.7 27.7-4.4l10.9-2.3c1.9-0.3 3.7-0.8 5.5-1.2 12.6-2.9 25.1-6.3 37.3-10.3l5.2-1.7C830.9 877.2 957.5 712.4 960 517.8V512c0-247.4-200.6-448-448-448z m346.5 594.3a377.1 377.1 0 0 1-200.2 200.2 377.5 377.5 0 0 1-292.6 0 377.1 377.1 0 0 1-200.2-200.2 377.5 377.5 0 0 1 0-292.6 377.1 377.1 0 0 1 200.2-200.2 377.5 377.5 0 0 1 292.6 0 377.1 377.1 0 0 1 200.2 200.2 377.5 377.5 0 0 1 0 292.6zM418.2 266.2a4 4 0 0 0-5.7 0l-45.3 45.2a3.9 3.9 0 0 0 0 5.6l192.6 192.2a3.9 3.9 0 0 1 0 5.6L367.2 707a3.9 3.9 0 0 0 0 5.6l45.3 45.2a4 4 0 0 0 5.7 0l195.4-194.9 39.7-39.6a15.9 15.9 0 0 0 0-22.6z" />
    </svg>
  );
}

export function Copy(props: IconProps) {
  return (
    <Base {...props}>
      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </Base>
  );
}
